/// <reference types="vite/client" />
import {
  ApplicationStatusApi,
  BackendProductsApi,
  Configuration,
  DealSummaryPDFApi,
  VendorSearchApi,
  VLALockApi,
  WorkbenchAnalystNotesApi,
  WorkbenchDashboardApi,
  WorkbenchDocsApi,
  WorkbenchDocsMessagesApi,
  WorkbenchDSCNotesApi,
  WorkbenchExitOptionsApi,
  WorkbenchInternalStatusHistoryApi,
  WorkbenchStipulationsApi,
  WorkbenchViewAllNotesApi,
  WorkbenchWorknumberApi,
  ZipCodeLookupApi,
} from "@credit-acceptance/step6-bff-client";

import { wrapBFFClientWithAuth } from "../lib/auth/authInterceptor";

// Fail loudly at module load if the BFF base URL is missing — otherwise
// requests silently fall through to the wrong path (e.g. `/stipulations`
// at the page-hosting origin) and surface as a confusing 404.
const BFF_BASE_URL = import.meta.env.VITE_STEP6_BFF_BASE_URL;
if (!BFF_BASE_URL) {
  throw new Error(
    "VITE_STEP6_BFF_BASE_URL is not set. Check the env file for the current mode (.env, .env.test, .env.qa, …)."
  );
}

const config = new Configuration({
  basePath: BFF_BASE_URL,
  // Send the oauth2-proxy session cookie on every BFF request.
  baseOptions: { withCredentials: true },
});

export const backendProductsApi = wrapBFFClientWithAuth(new BackendProductsApi(config));

export const vendorSearchApi = wrapBFFClientWithAuth(new VendorSearchApi(config));

// DSC Workbench dashboard aggregation. `getWorkbench(approvalId)` hits
// GET /api/v1/approvals/{approvalId}/workbench/Dashboard and returns a
// DscWorkbenchDashboardResponse.
export const dscWorkbenchApi = wrapBFFClientWithAuth(new WorkbenchDashboardApi(config));

// VLA In-Use (deal) lock — pessimistic single-holder lock backing OR-20985.
// All three verbs hit /api/v1/workbench/applications/{approvalId}/vlas-in-use:
//   acquireLock (PUT)  -> 200 with VlaLockStatusResponse, or 409 if held by another user
//   getLockStatus(GET) -> 200 with { isVlaLocked, lockedByUserId, lockedByUserName }
//   releaseLock (DELETE) -> 200 (idempotent; 404 only if the VLA itself is missing)
export const vlaLockApi = wrapBFFClientWithAuth(new VLALockApi(config));

// Application status + phase. `getApplicationStatus(approvalId)` hits
// GET /api/v1/workbench/applications/{approvalId}/status and returns an
// ApplicationStatusResponse ({ statusDescription, phaseDescription }), proxied
// by the BFF to the Credit Application Service.
export const applicationStatusApi = wrapBFFClientWithAuth(new ApplicationStatusApi(config));

// Workbench exit screen options. `getNextStatusPhases(approvalId, currentStatus,
// roleName)` hits GET /api/v1/workbench/{approvalId}/next-status-phases and
// returns a NextStatusPhaseResponse ({ currentStatus, nextStatusList,
// currentPhase, phasesList, manualPhase }). The BFF forwards to recap-service to
// produce the next-status / phase dropdown options shown when an analyst exits
// the account.
export const workbenchExitOptionsApi = wrapBFFClientWithAuth(new WorkbenchExitOptionsApi(config));

// Aggregated documents + messages. `getDocsMessages(appId)` hits
// GET /api/v1/workbench/applications/{appId}/docs-messages and returns a
// composite of appxtender/piston docs and messages. The generated client types
// the response as `object`; the query hook casts it to `DocumentsAndMessagesResponse`
// (see features/documents/api/types.ts). The app passes the route `approvalId`
// as `appId`, matching the sibling `applications/{…}` status + VLA-lock reads.
export const workbenchDocumentsAndMessagesApi = wrapBFFClientWithAuth(new WorkbenchDocsMessagesApi(config));

// DSC Pre-approval Change History (OR-21039). `getDscNotes(approvalId, phase)`
// hits GET /api/v1/workbench/{approvalId}/dsc-notes?phase=all and returns an
// array of DscNoteResponse change-history rows (date, attributeName, oldValue,
// newValue, …), forwarded by the BFF from recap-service. Empty histories come
// back as 200 with an empty array (never 404). caps2 step-6 surfaces this as a
// popup; the Workbench renders it as an internal-audit drawer tab.
export const workbenchDscNotesApi = wrapBFFClientWithAuth(new WorkbenchDSCNotesApi(config));

// Analyst notes (OR-22625). Both verbs hit
// /api/v1/workbench/applications/{approvalId}/analyst-notes, forwarded by the
// BFF to recap-service:
//   getAnalystNotes(approvalId)         (GET)  -> AnalystNoteResponse[] ({ createdDatetime,
//     authorUserId, note }); an empty history comes back as 200 with an empty array (never 404).
//   saveAnalystNote(approvalId, body,   (POST) -> 204 No Content; recap derives the author +
//     idempotencyKey?)                          timestamp server-side. An optional Idempotency-Key
//                                               dedups accidental double-submits.
// caps2 step-6 surfaced these as a popup; the Workbench renders them in the
// amber Analyst Notes FAB modal.
export const workbenchAnalystNotesApi = wrapBFFClientWithAuth(new WorkbenchAnalystNotesApi(config));

// Analyst document uploads (Send Proofs). `generateUploadUrl(appId, { category,
// filename, dealerId })` hits PUT /api/v1/workbench/applications/{appId}/docs/upload-url
// and returns { uploadUrl, proofFileId }. The app then PUTs the raw file bytes
// directly to the presigned `uploadUrl` (see features/documents/api/uploadProof.ts)
// — that S3 request deliberately bypasses this authenticated client.
// `applyDocAction(appId, docId, { checkIn?, moveToApprovalNumber?, moveToState? })`
// hits POST /api/v1/workbench/applications/{appId}/docs/{docId}/actions (see
// features/documents/api/useApplyDocumentAction.ts).
export const workbenchDocumentsApi = wrapBFFClientWithAuth(new WorkbenchDocsApi(config));

// Work Number (purchased employer info) — per-applicant income verification,
// forwarded by the BFF to recap-service. All three verbs hit
// /api/v1/workbench/{approvalId}/applicants/{applicantId}/worknumber:
//   getEligibility (GET .../eligibility) -> object: the applicant's eligibility snapshot
//   getHistory     (GET .../history)     -> object: previously-pulled verification history
//   pullWorkNumber (POST)                -> object: freshly-pulled history (dedup via Idempotency-Key)
// The generated client types every response as `object`; the query hooks cast to
// the hand-written view models in features/review/sections/Signers/api/worknumber.types.ts.
export const workbenchWorknumberApi = wrapBFFClientWithAuth(new WorkbenchWorknumberApi(config));

// Stipulations panel view (OR-22754). `getStipulations(approvalId, openOnly?)`
// hits GET /api/v1/workbench/applications/{approvalId}/stipulations and returns a
// StipulationListResponse, forwarded verbatim by the BFF from stipulation-service.
// `updatePorException` for OR-20983 is auto-generated (W6 endpoint).
export const workbenchStipulationsApi = wrapBFFClientWithAuth(new WorkbenchStipulationsApi(config));

// Internal-tab View All Notes (OR-22848). `getViewAllNotes(approvalId, filter?)`
// hits GET /api/v1/workbench/applications/{approvalId}/view-all-notes and returns
// a ViewAllNotesResponseView ({ filter, notes: ViewAllNotesEntryView[] }),
// forwarded by the BFF to recap-service. `filter` is opaque to the BFF —
// recap is the single source of truth for the accepted values (caps2 parity:
// All, CAPS, DEALER, DSC Analyst, Re-assign, Verification, Advance Adjustment).
// caps2 step-6 surfaced this as the Internal tab; the Workbench renders it as a
// tab inside the internal-audit drawer.
export const workbenchViewAllNotesApi = wrapBFFClientWithAuth(new WorkbenchViewAllNotesApi(config));

// Internal-tab Status History (OR-22868). `getInternalStatusHistory(approvalId)`
// hits GET /api/v1/workbench/applications/{approvalId}/internal-status-history
// and returns an InternalStatusHistoryResponseView ({ currentStatus, currentPhase,
// history: InternalStatusHistoryEntryView[] }), forwarded by the BFF to recap-service.
// caps2 step-6 surfaced this as the Internal tab; the Workbench renders it as a
// tab inside the internal-audit drawer.
export const workbenchInternalStatusHistoryApi = wrapBFFClientWithAuth(
  new WorkbenchInternalStatusHistoryApi(config)
);

// Step 5 Deal Summary PDF (OR-22900). `getDealSummaryPdf(appId, { responseType:
// "arraybuffer" })` hits GET /api/v1/workbench/{appId}/deal-summary/pdf and
// returns the raw PDF bytes, forwarded by the BFF from recap-service.
export const dealSummaryPdfApi = wrapBFFClientWithAuth(new DealSummaryPDFApi(config));

// ZIP -> city/state lookup. `lookupZipCode(zip)` hits
// GET /api/v1/workbench/addresses/fromzip/{zip} and returns a ZipCodeResponse
// ({ zip, city, state }), proxied by the BFF to cast-facade-service.
export const zipLookupApi = wrapBFFClientWithAuth(new ZipCodeLookupApi(config));
